import React from "react";
import { useTranslation } from "react-i18next";
import { StatCard as UnifiedStatCard } from "../../../../ui/StatCard";
import { CostPriceKPIMetrics, HighestMarginItem, MostSoldItem } from "../../../../../utils/costPriceUtils";
import { CostTargets, DEFAULT_COST_TARGETS, getCostTargetForTab, isCostBad } from "../../../../../utils/costTargets";

// Icon-bg tints matched to each cost-price SVG icon's own colour.
const BG_TOTAL_DISHES = '#1570EF1A';   // total-dishes.svg (#1570EF)
const BG_AVG_MARGIN = '#0992501A';     // average-cost-margin.svg (#099250)
const BG_MOST_POPULAR = '#DB97291A';   // most-popular.svg (#DB9729)
const BG_HIGHEST_MARGIN = '#6172F31A'; // highest-margin.svg (#6172F3)

interface CardsProps {
    kpiMetrics: CostPriceKPIMetrics & {
        highestMarginItem: HighestMarginItem | null;
        mostSoldItem: MostSoldItem | null;
    };
    activeTab: 'all' | 'food' | 'drinks' | 'addons';
    costTargets?: CostTargets;
}

const Cards: React.FC<CardsProps> = ({ kpiMetrics, activeTab, costTargets = DEFAULT_COST_TARGETS }) => {
    const { t } = useTranslation();

    const target = getCostTargetForTab(activeTab, costTargets);

    const getPercentageColor = (percentage: number): string => {
        return isCostBad(percentage, target) ? "text-[#eb8d00]" : "text-[#16B364]";
    };

    // Format currency
    const formatCurrency = (value: number) =>
        new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(value);

    return (
        <div className="grid grid-cols-2 gap-3 md:gap-6 xl:grid-cols-4" data-onboarding="cost-kpis">
            <div className="h-full w-full" data-onboarding="cost-kpi-total">
                <UnifiedStatCard
                    icon={<img src="/assets/total-dishes.svg" alt="" className="w-6 h-6" />}
                    iconBg={BG_TOTAL_DISHES}
                    title={t('costPrice.totalAnalyzed')}
                    value={`${kpiMetrics.analyzedCount}/${kpiMetrics.totalCount}`}
                    mobileLayout="tile"
                />
            </div>
            <div className="h-full w-full" data-onboarding="cost-kpi-avg">
                <UnifiedStatCard
                    icon={<img src="/assets/average-cost-margin.svg" alt="" className="w-6 h-6" />}
                    iconBg={BG_AVG_MARGIN}
                    title={t('costPrice.avgCostPrice')}
                    value={`${kpiMetrics.avgCostPrice.toFixed(1)}%`}
                    subtitle={<>{t('costPrice.weighted')}: <span className={getPercentageColor(kpiMetrics.weightedCostPrice)}>{kpiMetrics.weightedCostPrice.toFixed(1)}%</span></>}
                    mobileLayout="tile"
                />
            </div>
            <div className="h-full w-full" data-onboarding="cost-kpi-popular">
                <UnifiedStatCard
                    icon={<img src="/assets/most-popular.svg" alt="" className="w-6 h-6" />}
                    iconBg={BG_MOST_POPULAR}
                    title={t('costPrice.mostPopular')}
                    value={kpiMetrics.mostSoldItem ? kpiMetrics.mostSoldItem.quantitySold.toString() : "-"}
                    subtitle={kpiMetrics.mostSoldItem?.itemName}
                    mobileLayout="tile"
                />
            </div>
            <div className="h-full w-full" data-onboarding="cost-kpi-margin">
                <UnifiedStatCard
                    icon={<img src="/assets/highest-margin.svg" alt="" className="w-6 h-6" />}
                    iconBg={BG_HIGHEST_MARGIN}
                    title={t('costPrice.highestMargin')}
                    value={kpiMetrics.highestMarginItem ? formatCurrency(kpiMetrics.highestMarginItem.margin) : "-"}
                    subtitle={kpiMetrics.highestMarginItem?.itemName}
                    mobileLayout="tile"
                />
            </div>
        </div>
    );
};

export default Cards;